import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

/* ============================================================
 * KEYFRAMES
 * ============================================================ */
const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;

const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;

const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/*
 * Modal centralizado via position:fixed + translate(-50%,-50%).
 * O keyframe PRECISA preservar esse translate em ambos frames,
 * senão a centralização é sobrescrita e o modal "vem de baixo".
 */
const modalIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -50%) scale(0.98); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const spinKf = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

/* ============================================================
 * MIXINS
 * ============================================================ */
const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;

const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;

const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

/* ============================================================
 * CONTAINER
 * ============================================================ */
export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em;
  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  /* ============================================================
   * HEADER
   * ============================================================ */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 14px;
    flex-shrink: 0;

    .header-icon { display: none; }

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "USU\u00c1RIOS \u00b7 GEST\u00c3O DE ACESSO";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};
          background: transparent !important;
          padding: 0 !important;
          border: none !important;
          opacity: 1 !important;

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandGlow};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }
        }

        .update-tag {
          ${mono};
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          font-size: 0.9em;
          background: transparent !important;
          padding: 0 !important;
          border: none !important;

          &::before {
            content: "\u00b7 ";
            opacity: 0.5;
          }
        }
      }
    }
  }

  /* ============================================================
   * KPI CARDS (5 cols) \u2014 header-stats
   * ============================================================ */
  .header-stats,
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    @media (max-width: 1300px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 800px)  { grid-template-columns: repeat(2, 1fr); }
  }

  .stat-card,
  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;
    gap: 8px;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }
  }

  /* layout antigo (stat-card) \u2014 thumb + texto inline */
  .stat-card {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;

    .stat-icon {
      width: auto;
      height: auto;
      background: transparent !important;
      border: none !important;
      padding: 0 !important;
      color: ${({ theme }) => theme.text};
      display: inline-flex;
      align-items: center;
      opacity: 0.85;
      flex-shrink: 0;

      svg { font-size: 0.9em; }
    }

    > div:last-child {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .stat-value {
      ${mono};
      font-size: 1.6em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.02em;
      line-height: 1.1;
    }

    .stat-label {
      ${eyebrow};
      font-size: 0.6em;
      opacity: 0.5;
      margin-top: 2px;
    }

    /* Variantes \u2014 cor APENAS no \u00edcone */
    &:nth-child(1) .stat-icon { color: ${tokens.brand}; }       /* Total */
    &:nth-child(2) .stat-icon { color: #3b82f6; }                /* Ativos */
    &:nth-child(3) .stat-icon { color: ${tokens.danger}; }       /* Inativos */
    &:nth-child(4) .stat-icon { color: ${tokens.warn}; }         /* Admins */
    &:nth-child(5) .stat-icon { color: #8b5cf6; }                /* Comuns */
  }

  /* ============================================================
   * TOOLBAR
   * ============================================================ */
  .toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 4px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 10px;
      flex: 1;
      min-width: 0;
      flex-wrap: wrap;
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    /* Filter pills mono uppercase */
    .filter-btn {
      ${mono};
      padding: 7px 14px;
      background: transparent;
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
      font-size: 0.66em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      white-space: nowrap;

      &:hover { opacity: 0.9; }

      &.active {
        background: ${tokens.brandSoft};
        color: ${tokens.brand};
        border-color: ${tokens.brandRing};
        opacity: 1;
      }
    }

    .btn-new-user {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: ${tokens.brand};
      border: 1px solid ${tokens.brand};
      border-radius: ${tokens.radiusMd};
      color: white;
      font-family: ${tokens.fontSans};
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      letter-spacing: -0.005em;
      white-space: nowrap;

      svg { font-size: 0.95em; }

      &:hover:not(:disabled) {
        background: ${tokens.brandHover};
        border-color: ${tokens.brandHover};
        transform: translateY(-1px);
      }
    }

    .search-input {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      max-width: 360px;
      min-width: 200px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      svg {
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        flex-shrink: 0;
      }

      input {
        flex: 1;
        background: transparent;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        letter-spacing: -0.005em;

        &::placeholder { opacity: 0.4; }
      }
    }
  }

  /* ============================================================
   * MAIN CONTENT / USERS GRID
   * ============================================================ */
  .main-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    min-height: 0;
    margin-top: 6px;
    padding: 4px 6px 24px 0;

    &::-webkit-scrollbar { width: 5px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => hairline(theme, true)};
      border-radius: 3px;
    }
  }

  .users-container { width: 100%; }

  .users-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;

    @media (max-width: 1400px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 720px)  { grid-template-columns: 1fr; }
  }

  /* ============================================================
   * USER CARD
   * ============================================================ */
  .user-card {
    position: relative;
    ${({ theme }) => cardBase(theme)};
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-height: 200px;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
      .user-card-footer { opacity: 1; }
    }

    /* HEADER \u2014 avatar + nome + email */
    .user-card-header {
      display: grid;
      grid-template-columns: 46px 1fr;
      gap: 12px;
      align-items: center;
      padding-right: 4px;

      .user-avatar {
        display: inline-flex;
      }

      .avatar-circle {
        width: 46px;
        height: 46px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: white;
        flex-shrink: 0;

        p {
          ${mono};
          margin: 0;
          font-size: 1em;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: white;
        }
      }

      .user-info {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;

        h5 {
          margin: 0;
          font-size: 1.02em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          text-transform: capitalize;
        }

        .user-email {
          margin: 0;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          ${mono};
          font-size: 0.72em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          letter-spacing: 0.01em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          svg { font-size: 1em; opacity: 0.7; flex-shrink: 0; }
        }
      }
    }

    /* BODY \u2014 role + last-access + badges */
    .user-card-body {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .user-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      flex-wrap: wrap;
    }

    .role-badge {
      ${mono};
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      border-radius: ${tokens.radiusPill};
      font-size: 0.68em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      border: 1px solid currentColor;
      white-space: nowrap;
    }

    .last-access {
      ${mono};
      font-size: 0.66em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      letter-spacing: 0.01em;
    }

    /* Badges de permiss\u00f5es (full access OR per-module) */
    .user-permissions-badges {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      align-items: center;
    }

    .full-access-badge {
      ${mono};
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 4px 10px;
      border-radius: ${tokens.radiusPill};
      background: ${tokens.brandSoft};
      color: ${tokens.brand};
      border: 1px solid ${tokens.brandRing};
      font-size: 0.66em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      white-space: nowrap;

      svg { font-size: 0.95em; }
    }

    .badge {
      width: 26px;
      height: 26px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: ${tokens.radiusSm};
      border: 1px solid ${({ theme }) => hairline(theme)};
      background: transparent;
      color: ${({ theme }) => theme.text};
      opacity: 0.3;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      svg { font-size: 0.9em; }

      &[data-access="true"] {
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
        opacity: 1;
      }
    }

    /* FOOTER \u2014 action buttons reveal on hover */
    .user-card-footer {
      display: flex;
      gap: 6px;
      justify-content: flex-end;
      margin-top: auto;
      padding-top: 8px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      opacity: 0;
      transition: opacity ${tokens.durBase} ${tokens.easeOut};

      .btn-edit,
      .btn-delete {
        width: 28px;
        height: 28px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusSm};
        color: ${({ theme }) => theme.text};
        opacity: 0.65;
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        svg { font-size: 0.85em; }

        &:hover { opacity: 1; }
      }

      .btn-edit:hover {
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }

      .btn-delete:hover {
        background: ${tokens.dangerSoft};
        border-color: rgba(239, 68, 68, 0.22);
        color: ${tokens.danger};
      }
    }
  }

  /* ============================================================
   * EMPTY STATE
   * ============================================================ */
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 110px 20px 80px;
    background: transparent;
    border: none;
    text-align: center;

    > svg {
      width: 64px;
      height: 64px;
      padding: 18px;
      box-sizing: content-box;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: 50%;
      color: ${tokens.brand};
      opacity: 0.7;
      font-size: 1em;
    }

    h4 {
      margin: 0;
      font-size: 1em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      ${mono};
      font-size: 0.74em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      letter-spacing: 0.01em;
      max-width: 280px;
      line-height: 1.55;
    }
  }

  /* ============================================================
   * MODAIS \u2014 base shared
   * ============================================================ */
  /* modais movidos para ModalRoot (portal) */
`;

/* ============================================================
 * MODAL ROOT — portalizado pro document.body.
 * Renderizar os modais aqui (via createPortal) evita que o
 * zoom do Display Overlay e containing-blocks da arvore prendam
 * o position:fixed (causava o modal "vir de baixo").
 * ============================================================ */
export const ModalRoot = styled.div`
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.82);
    backdrop-filter: blur(10px) saturate(140%);
    z-index: 9998;
    animation: ${fadeInOnly} ${tokens.durBase} ${tokens.easeOut};
  }

  .edit-user-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    max-width: 580px;
    height: min(720px, 90vh);
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusXl};
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
    z-index: 9999;
    animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};

    /* HEADER */
    .modal-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 14px;
      padding: 16px 20px 14px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      flex-shrink: 0;

      .modal-header-content {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 4px;

        h3 {
          margin: 2px 0 0 0;
          font-size: 1.05em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.015em;
          line-height: 1.2;

          &::before {
            content: "USU\u00c1RIOS";
            display: block;
            ${eyebrow};
            font-size: 0.58em;
            opacity: 0.4;
            margin-bottom: 2px;
          }
        }

        .modal-subtitle {
          margin: 0;
          ${mono};
          font-size: 0.68em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          letter-spacing: 0.01em;
        }
      }

      .close-btn {
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusMd};
        color: ${({ theme }) => theme.text};
        opacity: 0.65;
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};
        font-size: 1.2em;
        font-family: inherit;

        &:hover {
          opacity: 1;
          border-color: ${({ theme }) => hairline(theme, true)};
        }
      }
    }

    /* STEP INDICATOR (invite) */
    .step-indicator {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 20px 10px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      flex-shrink: 0;

      .step {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        ${mono};
        font-size: 0.62em;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        transition: opacity ${tokens.durBase} ${tokens.easeOut};

        .step-number {
          width: 22px;
          height: 22px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: ${({ theme }) => surface(theme, 1)};
          border: 1px solid ${({ theme }) => hairline(theme)};
          font-size: 1em;
          font-weight: 600;
        }

        &.active {
          opacity: 1;
          color: ${tokens.brand};

          .step-number {
            background: ${tokens.brandSoft};
            border-color: ${tokens.brand};
            color: ${tokens.brand};
            box-shadow: 0 0 0 3px ${tokens.brandRing};
          }
        }

        &.completed {
          opacity: 0.7;

          .step-number {
            background: ${tokens.brand};
            border-color: ${tokens.brand};
            color: white;
          }
        }
      }

      .step-divider {
        flex: 1;
        height: 1px;
        background: ${({ theme }) => hairline(theme)};
      }
    }

    /* TABS (edit) */
    .modal-tabs {
      display: flex;
      gap: 0;
      padding: 0 20px;
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};
      flex-shrink: 0;

      .tab {
        ${mono};
        padding: 11px 16px;
        background: transparent;
        border: none;
        border-bottom: 2px solid transparent;
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
        font-size: 0.66em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};
        font-family: ${tokens.fontMono};

        &:hover { opacity: 0.75; }

        &.active {
          opacity: 1;
          color: ${tokens.brand};
          border-bottom-color: ${tokens.brand};
        }
      }
    }

    /* BODY (scroll) */
    .modal-body {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 18px 20px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      min-height: 0;

      &::-webkit-scrollbar { width: 4px; }
      &::-webkit-scrollbar-track { background: transparent; }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => hairline(theme, true)};
        border-radius: 3px;
      }
    }

    /* INVITE HERO (etapa 1) */
    .invite-step-email {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .invite-hero {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 14px 16px;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: ${tokens.radiusMd};

      .invite-hero-icon {
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => surface(theme, 0)};
        border: 1px solid ${tokens.brandRing};
        border-radius: 50%;
        color: ${tokens.brand};
        flex-shrink: 0;

        svg { font-size: 0.95em; }
      }

      p {
        margin: 0;
        font-size: 0.78em;
        color: ${({ theme }) => theme.text};
        opacity: 0.75;
        line-height: 1.5;
        letter-spacing: -0.005em;
      }
    }

    .invite-note {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 10px 12px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusSm};

      svg {
        color: ${tokens.warn};
        opacity: 0.85;
        font-size: 0.95em;
        flex-shrink: 0;
        margin-top: 2px;
      }

      span {
        font-size: 0.72em;
        line-height: 1.5;
        color: ${({ theme }) => theme.text};
        opacity: 0.65;
        letter-spacing: -0.005em;
      }
    }

    /* FORM GROUP \u2014 inputs e selects */
    .form-group {
      display: flex;
      flex-direction: column;
      gap: 6px;

      label {
        ${eyebrow};
        font-size: 0.58em;
        opacity: 0.55;
        display: inline-flex;
        align-items: center;
        gap: 5px;

        svg { font-size: 1.05em; opacity: 0.8; }
      }

      input,
      select {
        padding: 9px 12px;
        background: ${({ theme }) => surface(theme, 1)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusSm};
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        letter-spacing: -0.005em;
        outline: none;
        transition: border-color ${tokens.durBase} ${tokens.easeOut};

        &::placeholder { opacity: 0.4; }
        &:focus { border-color: ${tokens.brandRing}; }

        &.error {
          border-color: ${tokens.danger};
        }
      }

      .form-hint {
        ${mono};
        font-size: 0.6em;
        letter-spacing: 0.01em;
        opacity: 0.7;

        &.validating { color: ${({ theme }) => theme.text}; opacity: 0.5; }
        &.error { color: ${tokens.danger}; opacity: 1; }
      }

      /* SUGGESTIONS LIST */
      .suggestions-list {
        margin-top: 8px;
        background: ${({ theme }) => surface(theme, 0)};
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusMd};
        max-height: 260px;
        overflow-y: auto;
        overflow-x: hidden;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);

        &::-webkit-scrollbar { width: 4px; }
        &::-webkit-scrollbar-track { background: transparent; }
        &::-webkit-scrollbar-thumb {
          background: ${({ theme }) => hairline(theme, true)};
          border-radius: 3px;
        }

        .suggestions-header {
          ${mono};
          padding: 10px 12px;
          font-size: 0.55em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: ${({ theme }) => theme.text};
          opacity: 0.45;
          border-bottom: 1px solid ${({ theme }) => hairline(theme)};
        }

        .suggestions-loading,
        .suggestions-empty {
          padding: 16px;
          text-align: center;
          ${mono};
          font-size: 0.72em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
        }

        .suggestion-item {
          display: grid;
          grid-template-columns: 36px 1fr auto auto;
          gap: 10px;
          align-items: center;
          padding: 10px 12px;
          cursor: pointer;
          border-bottom: 1px solid ${({ theme }) => hairline(theme)};
          transition: background ${tokens.durBase} ${tokens.easeOut};

          &:last-child { border-bottom: none; }
          &:hover {
            background: ${tokens.brandSoft};
          }

          .user-avatar {
            width: 36px;
            height: 36px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: ${tokens.brand};
            color: white;
            ${mono};
            font-size: 0.85em;
            font-weight: 600;
          }

          .user-info {
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 2px;

            h4 {
              margin: 0;
              font-size: 0.9em;
              font-weight: 500;
              color: ${({ theme }) => theme.text};
              letter-spacing: -0.01em;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }

            p {
              margin: 0;
              ${mono};
              font-size: 0.68em;
              color: ${({ theme }) => theme.text};
              opacity: 0.55;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
          }

          .user-role {
            ${mono};
            font-size: 0.58em;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            padding: 3px 8px;
            border: 1px solid ${({ theme }) => hairline(theme)};
            border-radius: ${tokens.radiusPill};
          }

          .check-icon {
            color: ${tokens.brand};
            font-size: 1.1em;
            opacity: 0.7;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
        }
      }
    }

    .error-message {
      ${mono};
      padding: 8px 12px;
      background: ${tokens.dangerSoft};
      border: 1px solid rgba(239, 68, 68, 0.22);
      border-radius: ${tokens.radiusSm};
      color: ${tokens.danger};
      font-size: 0.72em;
      letter-spacing: 0.01em;
    }

    /* USER PROFILE (Edit modal header summary) */
    .user-profile {
      display: grid;
      grid-template-columns: 56px 1fr;
      gap: 14px;
      align-items: center;
      padding: 12px 14px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      margin-bottom: 4px;

      .profile-thumb {
        display: inline-flex;
      }

      .profile-circle {
        width: 56px;
        height: 56px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: white;
        flex-shrink: 0;

        p {
          ${mono};
          margin: 0;
          font-size: 1.1em;
          font-weight: 600;
          letter-spacing: 0.02em;
          color: white;
        }
      }

      .profile-info {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;

        h4 {
          margin: 0;
          font-size: 1em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.015em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          text-transform: capitalize;
        }

        .profile-email {
          margin: 0;
          ${mono};
          font-size: 0.7em;
          color: ${({ theme }) => theme.text};
          opacity: 0.55;
          letter-spacing: 0.01em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .permission-badges {
        grid-column: 1 / -1;
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 8px;
        padding-top: 10px;
        border-top: 1px solid ${({ theme }) => hairline(theme)};
      }
    }

    /* PERMISSIONS LIST (shared invite + edit) */
    .permissions-list {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .permission-toggle-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;

      span {
        ${eyebrow};
        font-size: 0.58em;
        opacity: 0.55;
      }

      .toggle-all-btn {
        ${mono};
        padding: 5px 10px;
        background: transparent;
        border: 1px solid ${({ theme }) => hairline(theme)};
        border-radius: ${tokens.radiusSm};
        color: ${({ theme }) => theme.text};
        opacity: 0.75;
        font-size: 0.6em;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};
        font-family: ${tokens.fontMono};

        &:hover {
          opacity: 1;
          background: ${({ theme }) => surface(theme, 1)};
          border-color: ${tokens.brandRing};
          color: ${tokens.brand};
        }
      }
    }

    .permission-section {
      display: flex;
      flex-direction: column;
      gap: 6px;

      h5 {
        ${eyebrow};
        margin: 0 0 4px 0;
        font-size: 0.55em;
        opacity: 0.45;
      }
    }

    .permission-toggle-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 9px 12px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusSm};
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};

      &:hover {
        border-color: ${({ theme }) => hairline(theme, true)};
        background: ${({ theme }) => surface(theme, 0)};
      }

      &.enabled {
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
      }

      .permission-label {
        display: flex;
        align-items: center;
        gap: 9px;
        min-width: 0;

        .permission-icon {
          font-size: 1em;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
          flex-shrink: 0;
          transition: color ${tokens.durBase} ${tokens.easeOut};
        }

        span {
          font-size: 0.82em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.005em;
        }
      }

      &.enabled .permission-label .permission-icon {
        color: ${tokens.brand};
        opacity: 1;
      }

      .toggle-switch {
        width: 34px;
        height: 20px;
        border-radius: 10px;
        position: relative;
        transition: background ${tokens.durBase} ${tokens.easeOut};
        flex-shrink: 0;

        &.on { background: ${tokens.brand}; }
        &.off { background: ${({ theme }) => surface(theme, 2)}; }

        .toggle-knob {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: white;
          position: absolute;
          top: 3px;
          transition: left ${tokens.durBase} ${tokens.easeOut};
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }

        &.on .toggle-knob { left: 17px; }
        &.off .toggle-knob { left: 3px; }
      }
    }

    .empty-permissions {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 10px;
      padding: 40px 20px;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      text-align: center;

      p {
        margin: 0;
        ${mono};
        font-size: 0.78em;
        letter-spacing: 0.01em;
      }
    }

    .admin-access-message {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 14px 16px;
      background: ${tokens.brandSoft};
      border: 1px solid ${tokens.brandRing};
      border-radius: ${tokens.radiusMd};

      svg {
        font-size: 1.1em;
        color: ${tokens.brand};
        flex-shrink: 0;
        margin-top: 2px;
      }

      p {
        margin: 0;
        font-size: 0.82em;
        color: ${({ theme }) => theme.text};
        opacity: 0.85;
        line-height: 1.5;
        letter-spacing: -0.005em;
      }
    }

    /* FOOTER */
    .modal-footer {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      padding: 14px 22px 16px;
      border-top: 1px solid ${({ theme }) => hairline(theme)};
      background: ${({ theme }) => surface(theme, 0)};
      flex-shrink: 0;

      &::before {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg, transparent, ${({ theme }) => hairline(theme, true)}, transparent);
        opacity: 0.4;
        pointer-events: none;
      }

      button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        padding: 9px 18px;
        border-radius: ${tokens.radiusMd};
        font-family: ${tokens.fontSans};
        font-size: 0.8em;
        font-weight: 500;
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};
        letter-spacing: -0.005em;
        border: 1px solid transparent;

        svg { font-size: 0.95em; }

        &:disabled { opacity: 0.4; cursor: not-allowed; }
      }

      .btn-cancel {
        background: transparent;
        color: ${({ theme }) => theme.text};
        border-color: ${({ theme }) => hairline(theme)};
        opacity: 0.85;

        &:hover:not(:disabled) {
          opacity: 1;
          background: ${({ theme }) => surface(theme, 1)};
          border-color: ${({ theme }) => hairline(theme, true)};
        }
      }

      .btn-next-step,
      .btn-create,
      .btn-save {
        background: ${tokens.brand};
        color: white;
        border-color: ${tokens.brand};

        &:hover:not(:disabled) {
          background: ${tokens.brandHover};
          border-color: ${tokens.brandHover};
          transform: translateY(-1px);
        }
      }
    }

    .spinner-small {
      width: 12px;
      height: 12px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: ${spinKf} 0.8s linear infinite;
    }
  }
`;
